/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `sleep_assessment` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SleepAssessmentMesg : Mesg {
    public constructor() : super("SleepAssessment", Profile.MesgNum.SLEEP_ASSESSMENT)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** combined_awake_score. Average of awake_time_score and awakenings_count_score. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var combinedAwakeScore: UByte?
        get() = getFieldValue(COMBINED_AWAKE_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(COMBINED_AWAKE_SCORE_FIELD_NUM, value)

    /** awake_time_score. Score that evaluates the total time spent awake between sleep. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var awakeTimeScore: UByte?
        get() = getFieldValue(AWAKE_TIME_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AWAKE_TIME_SCORE_FIELD_NUM, value)

    /** awakenings_count_score. Score that evaluates the number of awakenings that interrupt sleep. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var awakeningsCountScore: UByte?
        get() = getFieldValue(AWAKENINGS_COUNT_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AWAKENINGS_COUNT_SCORE_FIELD_NUM, value)

    /** deep_sleep_score. Score that evaluates the amount of deep sleep. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var deepSleepScore: UByte?
        get() = getFieldValue(DEEP_SLEEP_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEEP_SLEEP_SCORE_FIELD_NUM, value)

    /** sleep_duration_score. Score that evaluates the quality of sleep based on sleep stages, heart-rate variability and possible awakenings during the night. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var sleepDurationScore: UByte?
        get() = getFieldValue(SLEEP_DURATION_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(SLEEP_DURATION_SCORE_FIELD_NUM, value)

    /** light_sleep_score. Score that evaluates the amount of light sleep. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var lightSleepScore: UByte?
        get() = getFieldValue(LIGHT_SLEEP_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(LIGHT_SLEEP_SCORE_FIELD_NUM, value)

    /** overall_sleep_score. Total score that summarizes the overall quality of sleep, combining sleep duration and quality. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var overallSleepScore: UByte?
        get() = getFieldValue(OVERALL_SLEEP_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(OVERALL_SLEEP_SCORE_FIELD_NUM, value)

    /** sleep_quality_score. Score that evaluates the quality of sleep based on sleep stages, heart-rate variability and possible awakenings during the night. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var sleepQualityScore: UByte?
        get() = getFieldValue(SLEEP_QUALITY_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(SLEEP_QUALITY_SCORE_FIELD_NUM, value)

    /** sleep_recovery_score. Score that evaluates stress and recovery during sleep. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var sleepRecoveryScore: UByte?
        get() = getFieldValue(SLEEP_RECOVERY_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(SLEEP_RECOVERY_SCORE_FIELD_NUM, value)

    /** rem_sleep_score. Score that evaluates the amount of REM sleep. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var remSleepScore: UByte?
        get() = getFieldValue(REM_SLEEP_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(REM_SLEEP_SCORE_FIELD_NUM, value)

    /** sleep_restlessness_score. Score that evaluates the amount of restlessness during sleep. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var sleepRestlessnessScore: UByte?
        get() = getFieldValue(SLEEP_RESTLESSNESS_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(SLEEP_RESTLESSNESS_SCORE_FIELD_NUM, value)

    /** awakenings_count. The number of awakenings during sleep. */
    public var awakeningsCount: UByte?
        get() = getFieldValue(AWAKENINGS_COUNT_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AWAKENINGS_COUNT_FIELD_NUM, value)

    /** interruptions_score. Score that evaluates the sleep interruptions. If valid: 0 (worst) to 100 (best). If unknown: FIT_UINT8_INVALID. */
    public var interruptionsScore: UByte?
        get() = getFieldValue(INTERRUPTIONS_SCORE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(INTERRUPTIONS_SCORE_FIELD_NUM, value)

    /** average_stress_during_sleep. Excludes stress during awake periods in the sleep window */
    public var averageStressDuringSleep: Double?
        get() = getFieldValue(AVERAGE_STRESS_DURING_SLEEP_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVERAGE_STRESS_DURING_SLEEP_FIELD_NUM, value)

    public companion object {
        public const val COMBINED_AWAKE_SCORE_FIELD_NUM: UByte = 0u
        public const val AWAKE_TIME_SCORE_FIELD_NUM: UByte = 1u
        public const val AWAKENINGS_COUNT_SCORE_FIELD_NUM: UByte = 2u
        public const val DEEP_SLEEP_SCORE_FIELD_NUM: UByte = 3u
        public const val SLEEP_DURATION_SCORE_FIELD_NUM: UByte = 4u
        public const val LIGHT_SLEEP_SCORE_FIELD_NUM: UByte = 5u
        public const val OVERALL_SLEEP_SCORE_FIELD_NUM: UByte = 6u
        public const val SLEEP_QUALITY_SCORE_FIELD_NUM: UByte = 7u
        public const val SLEEP_RECOVERY_SCORE_FIELD_NUM: UByte = 8u
        public const val REM_SLEEP_SCORE_FIELD_NUM: UByte = 9u
        public const val SLEEP_RESTLESSNESS_SCORE_FIELD_NUM: UByte = 10u
        public const val AWAKENINGS_COUNT_FIELD_NUM: UByte = 11u
        public const val INTERRUPTIONS_SCORE_FIELD_NUM: UByte = 14u
        public const val AVERAGE_STRESS_DURING_SLEEP_FIELD_NUM: UByte = 15u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("CombinedAwakeScore", 0u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[1] = Field("AwakeTimeScore", 1u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[2] = Field("AwakeningsCountScore", 2u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[3] = Field("DeepSleepScore", 3u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[4] = Field("SleepDurationScore", 4u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[5] = Field("LightSleepScore", 5u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[6] = Field("OverallSleepScore", 6u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[7] = Field("SleepQualityScore", 7u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[8] = Field("SleepRecoveryScore", 8u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[9] = Field("RemSleepScore", 9u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[10] = Field("SleepRestlessnessScore", 10u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[11] = Field("AwakeningsCount", 11u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[14] = Field("InterruptionsScore", 14u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[15] = Field("AverageStressDuringSleep", 15u, BaseType.UINT16, 100.0, 0.0, "", false)
            return fields
        }
    }
}
